import { useCallback, useEffect, useState } from 'react'
import { api, ErreurApi } from '../api'
import { Ecran } from '../composants/Ecran'
import { useToast } from '../etat/Toast'

interface Signalement {
  id: number
  page: string
  description: string
  vendeurNom: string
  creeLe: string
  statut: 'ouvert' | 'resolu'
}

const FORMAT = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

export function Bugs() {
  const { alerter, signaler } = useToast()
  const [bugs, setBugs] = useState<Signalement[] | null>(null)
  const [ouvert, setOuvert] = useState<number | null>(null)
  const [capture, setCapture] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<'ouvert' | 'tous'>('ouvert')

  const charger = useCallback(() => {
    api<{ bugs: Signalement[] }>('/bugs')
      .then((r) => setBugs(r.bugs))
      .catch((cause: unknown) => alerter(cause instanceof ErreurApi ? cause.message : 'Liste indisponible.'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(charger, [charger])

  const ouvrirCapture = async (id: number) => {
    if (ouvert === id) {
      setOuvert(null)
      setCapture(null)
      return
    }
    setOuvert(id)
    setCapture(null)
    try {
      const bug = await api<{ capture: string }>(`/bugs/${id}`)
      // Le serveur stocke le base64 nu (même logique que les photos
      // d'étiquettes) — il faut remettre le préfixe pour qu'un <img> l'affiche.
      setCapture(`data:image/jpeg;base64,${bug.capture}`)
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Capture indisponible.')
    }
  }

  const changerStatut = async (id: number, statut: 'ouvert' | 'resolu') => {
    try {
      await api(`/bugs/${id}`, { methode: 'PUT', corps: { statut } })
      signaler(statut === 'resolu' ? 'Marqué comme corrigé.' : 'Rouvert.')
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Impossible de changer le statut.')
    }
  }

  const supprimer = async (id: number) => {
    if (!window.confirm('Supprimer ce signalement ? C’est définitif.')) return
    try {
      await api(`/bugs/${id}`, { methode: 'DELETE' })
      signaler('Signalement supprimé.')
      if (ouvert === id) {
        setOuvert(null)
        setCapture(null)
      }
      charger()
    } catch (cause) {
      alerter(cause instanceof ErreurApi ? cause.message : 'Suppression impossible.')
    }
  }

  if (!bugs) {
    return (
      <Ecran titre="Bugs" sousTitre="Chargement…">
        <div className="vide">Lecture des signalements…</div>
      </Ecran>
    )
  }

  const visibles = filtre === 'ouvert' ? bugs.filter((b) => b.statut === 'ouvert') : bugs

  return (
    <Ecran
      titre="Bugs"
      sousTitre="Signalements déposés depuis l’écran de test"
      outils={
        <select
          className="saisie"
          style={{ marginLeft: 'auto' }}
          value={filtre}
          onChange={(e) => setFiltre(e.target.value as 'ouvert' | 'tous')}
        >
          <option value="ouvert">Ouverts seulement</option>
          <option value="tous">Tous ({bugs.length})</option>
        </select>
      }
    >
      <div className="zone-defilante">
        {visibles.length === 0 && (
          <div className="vide">
            {filtre === 'ouvert' ? 'Aucun bug ouvert — bien joué.' : 'Aucun signalement.'}
          </div>
        )}
        {visibles.map((bug) => (
          <section key={bug.id} className="bloc-fiche" style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div className="sourdine" style={{ fontSize: 12 }}>
                  {FORMAT.format(new Date(bug.creeLe))} · {bug.vendeurNom} · {bug.page}
                </div>
                <div style={{ marginTop: 4 }}>{bug.description}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button type="button" className="btn btn-fantome" onClick={() => void ouvrirCapture(bug.id)}>
                  {ouvert === bug.id ? 'Masquer' : 'Capture'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondaire"
                  onClick={() => void changerStatut(bug.id, bug.statut === 'ouvert' ? 'resolu' : 'ouvert')}
                >
                  {bug.statut === 'ouvert' ? 'Marquer corrigé' : 'Rouvrir'}
                </button>
                <button type="button" className="btn btn-fantome" onClick={() => void supprimer(bug.id)}>
                  Supprimer
                </button>
              </div>
            </div>
            {ouvert === bug.id && (
              <div style={{ marginTop: 10 }}>
                {capture ? (
                  <img
                    src={capture}
                    alt={`Capture d’écran du bug signalé sur ${bug.page}`}
                    style={{ maxWidth: '100%', border: '1px solid var(--color-neutral-300)' }}
                  />
                ) : (
                  <div className="vide">Chargement de la capture…</div>
                )}
              </div>
            )}
          </section>
        ))}
      </div>
    </Ecran>
  )
}
